import React, { useEffect, useMemo, useState } from 'react';
import { subscription } from '../../lib/api';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronRight,
  CreditCard,
  Crown,
  FileText,
  HelpCircle,
  Lock,
  Receipt,
  ShieldCheck,
  Sparkles,
  Star,
  Users,
  X,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { deriveSubscriptionView } from './subscriptionView';


const CHECKOUT_KEY_PREFIX = 'td_checkout_idempotency_';

const ROLE_PLAN_MATRIX = {
  athlete: new Set(['pro']),
  coach: new Set(['pro', 'coach']),
  admin: new Set(['pro', 'coach']),
};

const isPlanAvailableForRole = (role, planId) => {
  if (planId === 'free') return true;
  const allowedPlans = ROLE_PLAN_MATRIX[role];
  return Boolean(allowedPlans && allowedPlans.has(planId));
};

const SUBSCRIPTION_PLANS = [
  {
    id: 'free',
    nameKey: 'subscription.plans.free',
    price: 0,
    currency: 'RUB',
    period: 'forever',
    icon: Star,
    tone: 'neutral',
    features: ['basicTracking', 'upTo10', 'oneAthlete', 'basicStats', 'noExport'],
    limitations: ['noAnalytics', 'noVideo', 'noSupport'],
  },
  {
    id: 'pro',
    nameKey: 'subscription.plans.pro',
    price: 299,
    currency: 'RUB',
    period: 'perMonth',
    icon: Crown,
    tone: 'pro',
    popular: true,
    features: [
      'unlimitedTrainings',
      'advancedAnalytics',
      'videoAnalysis',
      'exportPdf',
      'prioritySupport',
      'unlimitedTournaments',
    ],
  },
  {
    id: 'coach',
    nameKey: 'subscription.plans.coach',
    price: 999,
    currency: 'RUB',
    period: 'perMonth',
    icon: Users,
    tone: 'coach',
    features: [
      'upTo20Athletes',
      'trainingPlans',
      'groupSessions',
      'groupAnalytics',
      'financialTracking',
      'prioritySupport',
    ],
  },
  {
    id: 'club',
    nameKey: 'subscription.plans.club',
    price: 2999,
    currency: 'RUB',
    period: 'perMonth',
    icon: Users,
    tone: 'club',
    features: [
      'upTo100Athletes',
      'clubBranding',
      'apiAccess',
      'personalManager',
      'customReports',
      'slaSupport',
    ],
  },
];

const FAQ_IDS = ['1', '2', '3', '4'];

const STATUS_META = {
  active: {
    labelKey: 'subscription.status.active',
    className: 'bg-lime-100 text-lime-800 border-lime-200',
    iconClass: 'text-lime-700',
  },
  trialing: {
    labelKey: 'subscription.status.trialing',
    className: 'bg-blue-100 text-blue-800 border-blue-200',
    iconClass: 'text-blue-700',
  },
  past_due: {
    labelKey: 'subscription.status.pastDue',
    className: 'bg-orange-100 text-orange-800 border-orange-200',
    iconClass: 'text-orange-700',
  },
  canceled: {
    labelKey: 'subscription.status.canceled',
    className: 'bg-red-100 text-red-800 border-red-200',
    iconClass: 'text-red-700',
  },
  none: {
    labelKey: 'subscription.status.none',
    className: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    iconClass: 'text-zinc-700',
  },
};

const PLAN_TONE = {
  free: {
    card: 'border-zinc-200',
    icon: 'border-zinc-200 bg-zinc-100 text-zinc-700',
    bar: 'from-zinc-300 to-zinc-500',
  },
  pro: {
    card: 'border-lime-200',
    icon: 'border-lime-200 bg-lime-100 text-lime-800',
    bar: 'from-lime-300 to-emerald-500',
  },
  coach: {
    card: 'border-purple-200',
    icon: 'border-purple-200 bg-purple-100 text-purple-800',
    bar: 'from-purple-400 to-violet-600',
  },
  club: {
    card: 'border-amber-200',
    icon: 'border-amber-200 bg-amber-100 text-amber-800',
    bar: 'from-amber-400 to-orange-500',
  },
};

const formatPlanPrice = (plan, t) => {
  if (plan.price === 0) return t('subscription.free');
  return `${plan.price} ${plan.currency}`;
};

const SubscriptionPage = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [processingPlan, setProcessingPlan] = useState(null);

  const loadSubscription = async () => {
    setLoading(true);
    setLoadError('');
    try {
      const response = await subscription.get();
      setCurrentSubscription(response.data);
    } catch (error) {
      console.error('Failed to load subscription:', error);
      setLoadError(t('subscription.loadError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSubscription();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpgrade = async (planId) => {
    setProcessingPlan(planId);
    try {
      if (planId === 'free') {
        await loadSubscription();
        return;
      }

      const storageKey = `${CHECKOUT_KEY_PREFIX}${planId}`;
      let idempotencyKey = null;
      try {
        idempotencyKey = sessionStorage.getItem(storageKey);
      } catch {
        // Checkout still works when sessionStorage is unavailable.
      }
      if (!idempotencyKey) {
        idempotencyKey =
          typeof crypto !== 'undefined' && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${planId}`;
        try {
          sessionStorage.setItem(storageKey, idempotencyKey);
        } catch {
          // Checkout still works without idempotency replay storage.
        }
      }

      const response = await subscription.createCheckout(planId, idempotencyKey);
      const checkoutUrl = response?.data?.checkout_url;

      if (checkoutUrl) {
        window.location.href = checkoutUrl;
        return;
      }

      await loadSubscription();
    } catch (error) {
      console.error('Failed to upgrade subscription:', error);
    } finally {
      setProcessingPlan(null);
    }
  };

  const localeDateString = (dateStr) => {
    const locale =
      i18n.language === 'ru'
        ? 'ru-RU'
        : i18n.language === 'fr'
        ? 'fr-FR'
        : 'en-GB';
    return new Date(dateStr).toLocaleDateString(locale);
  };

  const { currentPlanId, currentPeriodEnd, autoRenew } =
    deriveSubscriptionView(currentSubscription);
  const currentRole = user?.role;

  const visiblePlans = useMemo(
    () =>
      SUBSCRIPTION_PLANS.filter(
        (plan) => plan.id !== 'club' || currentPlanId === 'club'
      ),
    [currentPlanId]
  );

  const currentPlan =
    SUBSCRIPTION_PLANS.find((plan) => plan.id === currentPlanId) ||
    SUBSCRIPTION_PLANS[0];
  const CurrentPlanIcon = currentPlan.icon;
  const subscriptionStatus = currentSubscription?.status || 'none';
  const statusMeta = STATUS_META[subscriptionStatus] || {
    labelKey: null,
    className: 'bg-zinc-100 text-zinc-700 border-zinc-200',
    iconClass: 'text-zinc-700',
  };
  const getPlanName = (plan) => t(plan.nameKey || `subscription.plans.${plan.id}`);
  const statusLabel = statusMeta.labelKey ? t(statusMeta.labelKey) : subscriptionStatus;
  const currentPlanName = getPlanName(currentPlan);
  const currentPlanTone = PLAN_TONE[currentPlan.id] || PLAN_TONE.free;

  const availableUpgradeCount = visiblePlans.filter(
    (plan) => plan.id !== currentPlanId && isPlanAvailableForRole(currentRole, plan.id)
  ).length;

  const auditEvents = Array.isArray(currentSubscription?.audit_events)
    ? currentSubscription.audit_events
    : [];

  const summaryCards = [
    {
      label: t('subscription.currentPlan'),
      value: currentPlanName,
      detail: statusLabel,
      Icon: CurrentPlanIcon,
      color: currentPlan.id === 'pro' ? 'text-lime-700' : currentPlan.id === 'coach' ? 'text-purple-700' : 'text-zinc-700',
    },
    {
      label: t('subscription.renewal'),
      value: autoRenew ? t('subscription.on') : t('subscription.off'),
      detail: currentPeriodEnd ? localeDateString(currentPeriodEnd) : t('subscription.noPeriodEnd'),
      Icon: ShieldCheck,
      color: autoRenew ? 'text-emerald-700' : 'text-orange-700',
    },
    {
      label: t('subscription.upgradePaths'),
      value: availableUpgradeCount,
      detail: t('subscription.availableForRole'),
      Icon: Sparkles,
      color: 'text-blue-700',
    },
    {
      label: t('subscription.billingEvents'),
      value: auditEvents.length,
      detail: t('subscription.auditRecordsReturned'),
      Icon: Receipt,
      color: 'text-zinc-700',
    },
  ];

  const comparisonFeatures = [
    'basicTracking',
    'advancedAnalytics',
    'videoAnalysis',
    'exportPdf',
    'prioritySupport',
  ];

  const planHasFeature = (plan, featureKey) => plan.features.includes(featureKey);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f6f7f4] px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-8" data-testid="subscription-loading">
        <div className="mx-auto max-w-7xl space-y-6">
          <div className="rounded-3xl bg-zinc-950 p-8">
            <div className="h-5 w-36 animate-pulse rounded bg-white/15" />
            <div className="mt-6 h-12 w-2/3 animate-pulse rounded bg-white/15" />
            <div className="mt-4 h-4 w-1/2 animate-pulse rounded bg-white/10" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[0, 1, 2, 3].map((item) => (
              <div key={item} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                <div className="h-4 w-24 animate-pulse rounded bg-zinc-200" />
                <div className="mt-5 h-8 w-16 animate-pulse rounded bg-zinc-100" />
              </div>
            ))}
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            {[0, 1, 2].map((item) => (
              <div key={item} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
                <div className="h-1.5 rounded bg-zinc-100" />
                <div className="mt-5 h-12 w-12 animate-pulse rounded-2xl bg-zinc-200" />
                <div className="mt-5 h-5 w-32 animate-pulse rounded bg-zinc-200" />
                <div className="mt-5 space-y-2">
                  <div className="h-3 w-full animate-pulse rounded bg-zinc-100" />
                  <div className="h-3 w-4/5 animate-pulse rounded bg-zinc-100" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-[#f6f7f4] px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-8" data-testid="subscription-page">
        <div className="mx-auto max-w-3xl rounded-3xl border border-rose-200 bg-rose-50 p-8 text-center shadow-sm">
          <AlertTriangle className="mx-auto text-rose-600" size={40} />
          <h1 className="mt-4 text-2xl font-semibold tracking-tight text-rose-950">{t('subscription.loadErrorTitle')}</h1>
          <p className="mt-2 text-sm leading-6 text-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={loadSubscription}
            className="mt-6 inline-flex min-h-11 items-center justify-center rounded-full bg-rose-600 px-5 text-sm font-semibold text-white"
          >
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f6f7f4] px-4 pb-24 pt-4 lg:px-8 lg:pb-10 lg:pt-8" data-testid="subscription-page">
      <div className="mx-auto max-w-7xl space-y-6">
        <section className="relative overflow-hidden rounded-3xl bg-zinc-950 p-6 text-white shadow-2xl shadow-zinc-300/60 md:p-8">
          <div className="pointer-events-none absolute inset-0 opacity-25">
            <div className="absolute -right-20 top-6 h-60 w-60 rounded-full border border-lime-300" />
            <div className="absolute bottom-8 left-8 h-px w-72 rotate-6 bg-lime-300" />
            <div className="absolute bottom-20 left-1/2 h-px w-80 -rotate-12 bg-white/40" />
          </div>

          <div className="relative grid gap-8 lg:grid-cols-[minmax(0,1fr)_340px] lg:items-end">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-lime-200">
                <span className="h-2 w-2 rounded-full bg-lime-300" />
                {t('subscription.heroKicker')}
              </div>
              <h1 className="max-w-3xl text-4xl font-semibold tracking-tight md:text-5xl">{t('subscription.title')}</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-zinc-300 md:text-base">
                {t('subscription.heroBody')}
              </p>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/10 p-5 backdrop-blur" data-testid="current-plan-hero">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-300">{t('subscription.currentPlanLabel')}</span>
                <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusMeta.className}`}>
                  {statusLabel}
                </span>
              </div>
              <div className="flex items-center gap-4">
                <div className={`flex h-14 w-14 items-center justify-center rounded-2xl border ${currentPlanTone.icon}`}>
                  <CurrentPlanIcon size={26} />
                </div>
                <div>
                  <h2 className="text-3xl font-semibold tracking-tight text-white">{currentPlanName}</h2>
                  <p className="mt-1 text-sm text-zinc-300">
                    {currentPeriodEnd ? localeDateString(currentPeriodEnd) : t('subscription.free')}
                  </p>
                </div>
              </div>
              <div className="mt-5 flex items-center gap-2 text-sm text-zinc-300">
                <CheckCircle2 className={statusMeta.iconClass} size={17} />
                <span>{autoRenew ? t('subscription.autoRenew') : t('subscription.noAutoRenew')}</span>
              </div>
            </div>
          </div>
        </section>

        {(subscriptionStatus === 'past_due' || subscriptionStatus === 'canceled') && (
          <div className={`rounded-2xl border p-5 shadow-sm ${
            subscriptionStatus === 'past_due'
              ? 'border-orange-200 bg-orange-50 text-orange-900'
              : 'border-red-200 bg-red-50 text-red-900'
          }`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className={subscriptionStatus === 'past_due' ? 'text-orange-700' : 'text-red-700'} size={22} />
              <div>
                <h2 className="font-semibold tracking-tight">
                  {subscriptionStatus === 'past_due' ? t('subscription.paymentAttentionNeeded') : t('subscription.subscriptionCanceled')}
                </h2>
                <p className="mt-1 text-sm leading-6">
                  {subscriptionStatus === 'past_due'
                    ? t('subscription.pastDueBody')
                    : t('subscription.canceledBody')}
                </p>
              </div>
            </div>
          </div>
        )}

        <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {summaryCards.map(({ label, value, detail, Icon, color }) => (
            <div key={label} className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <span className="text-sm font-medium text-zinc-500">{label}</span>
                <Icon className={color} size={20} />
              </div>
              <div className={`text-3xl font-semibold tracking-tight ${color}`}>{value}</div>
              <p className="mt-1 truncate text-sm text-zinc-500">{detail}</p>
            </div>
          ))}
        </section>

        <section className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_340px]">
          <div className="space-y-6">
            <div>
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <h2 className="text-2xl font-semibold tracking-tight text-zinc-950">{t('subscription.choosePlan')}</h2>
                  <p className="mt-1 text-sm text-zinc-500">{t('subscription.checkoutEndpointBody')}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                {visiblePlans.map((plan) => {
                  const isCurrentPlan = currentPlanId === plan.id;
                  const isProcessing = processingPlan === plan.id;
                  const isUnavailable = !isPlanAvailableForRole(currentRole, plan.id);
                  const PlanIcon = plan.icon;
                  const tone = PLAN_TONE[plan.id] || PLAN_TONE.free;

                  const cta = isCurrentPlan
                    ? { label: t('subscription.btnCurrent'), disabled: true }
                    : isUnavailable
                    ? { label: t('subscription.btnUnavailable'), disabled: true }
                    : isProcessing
                    ? { label: t('subscription.btnProcessing'), disabled: true }
                    : { label: t('subscription.btnChoose'), disabled: false };

                  return (
                    <article
                      key={plan.id}
                      data-testid={`plan-card-${plan.id}`}
                      className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-xl hover:shadow-zinc-200/70 ${
                        isCurrentPlan ? 'border-lime-300 ring-2 ring-lime-100' : tone.card
                      } ${isUnavailable && !isCurrentPlan ? 'opacity-75' : ''}`}
                    >
                      <div className={`h-1.5 bg-gradient-to-r ${isCurrentPlan ? 'from-lime-300 to-emerald-500' : tone.bar}`} />
                      <div className="p-5">
                        <div className="mb-5 flex items-start justify-between gap-4">
                          <div className={`flex h-12 w-12 items-center justify-center rounded-2xl border ${tone.icon}`}>
                            <PlanIcon size={23} />
                          </div>
                          <div className="flex flex-wrap justify-end gap-2">
                            {plan.popular && !isCurrentPlan && (
                              <span className="rounded-full border border-lime-200 bg-lime-100 px-3 py-1 text-xs font-semibold text-lime-800">
                                {t('subscription.mostPopular')}
                              </span>
                            )}
                            {isCurrentPlan && (
                              <span className="rounded-full border border-lime-200 bg-lime-100 px-3 py-1 text-xs font-semibold text-lime-800">
                                {t('subscription.btnCurrent')}
                              </span>
                            )}
                          </div>
                        </div>

                        <h3 className="text-xl font-semibold tracking-tight text-zinc-950">{getPlanName(plan)}</h3>
                        <div className="mt-2 flex items-baseline gap-2">
                          <span className="text-3xl font-semibold tracking-tight text-zinc-950">
                            {formatPlanPrice(plan, t)}
                          </span>
                          {plan.price > 0 && (
                            <span className="text-sm text-zinc-500">/ {t(`subscription.${plan.period}`)}</span>
                          )}
                        </div>

                        {isUnavailable && !isCurrentPlan && (
                          <p className="mt-3 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs font-semibold text-zinc-600">
                            {t('subscription.unavailableForRole')}
                          </p>
                        )}

                        <ul className="mt-5 space-y-3">
                          {plan.features.slice(0, 5).map((featureKey) => (
                            <li key={`feat-${plan.id}-${featureKey}`} className="flex items-start gap-2">
                              <Check size={16} className="mt-0.5 shrink-0 text-lime-600" aria-hidden="true" />
                              <span className="text-sm leading-5 text-zinc-600">
                                {t(`subscription.features.${featureKey}`)}
                              </span>
                            </li>
                          ))}
                          {(plan.limitations || []).slice(0, 2).map((featureKey) => (
                            <li key={`lim-${plan.id}-${featureKey}`} className="flex items-start gap-2">
                              <X size={16} className="mt-0.5 shrink-0 text-rose-500" aria-hidden="true" />
                              <span className="text-sm leading-5 text-zinc-500">
                                {t(`subscription.features.${featureKey}`)}
                              </span>
                            </li>
                          ))}
                        </ul>

                        <button
                          type="button"
                          disabled={cta.disabled}
                          onClick={() => handleUpgrade(plan.id)}
                          data-testid={`plan-cta-${plan.id}`}
                          className={`mt-6 inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-full px-5 text-sm font-semibold transition ${
                            isCurrentPlan
                              ? 'border border-lime-200 bg-lime-50 text-lime-800'
                              : isUnavailable
                              ? 'border border-zinc-200 bg-zinc-100 text-zinc-500'
                              : 'bg-zinc-950 text-white hover:bg-zinc-800'
                          } disabled:cursor-not-allowed`}
                        >
                          {cta.label}
                          {!cta.disabled && <ChevronRight size={17} />}
                        </button>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-5 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold tracking-tight text-zinc-950">{t('subscription.featureComparison')}</h2>
                  <p className="mt-1 text-sm text-zinc-500">{t('subscription.featureComparisonBody')}</p>
                </div>
                <FileText className="text-zinc-700" size={22} />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-[640px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-zinc-100 text-xs uppercase tracking-wide text-zinc-500">
                      <th className="py-3 pr-4 font-semibold">{t('subscription.feature')}</th>
                      {visiblePlans.map((plan) => (
                        <th key={plan.id} className="px-4 py-3 text-center font-semibold">{getPlanName(plan)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonFeatures.map((featureKey) => (
                      <tr key={featureKey} className="border-b border-zinc-100 last:border-0">
                        <td className="py-3 pr-4 font-medium text-zinc-700">
                          {t(`subscription.features.${featureKey}`)}
                        </td>
                        {visiblePlans.map((plan) => (
                          <td key={`${plan.id}-${featureKey}`} className="px-4 py-3 text-center">
                            {planHasFeature(plan, featureKey) ? (
                              <CheckCircle2 className="mx-auto text-lime-600" size={18} />
                            ) : (
                              <Lock className="mx-auto text-zinc-300" size={18} />
                            )}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <aside className="space-y-5">
            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm" data-testid="payment-methods-card">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('subscription.paymentMethods')}</h2>
                  <p className="text-sm text-zinc-500">{t('subscription.paymentProviderBody')}</p>
                </div>
                <CreditCard className="text-zinc-700" size={22} />
              </div>
              <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-5 text-center">
                <CreditCard className="mx-auto text-zinc-400" size={34} />
                <h3 className="mt-3 font-semibold text-zinc-950">{t('subscription.noPaymentMethod')}</h3>
                <p className="mt-2 text-sm leading-6 text-zinc-500">
                  {t('subscription.paymentMethodApiBody')}
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('subscription.billingHistory')}</h2>
                  <p className="text-sm text-zinc-500">{t('subscription.noInvoiceEndpoint')}</p>
                </div>
                <Receipt className="text-zinc-700" size={22} />
              </div>
              {auditEvents.length > 0 ? (
                <div className="space-y-3">
                  {auditEvents.slice(0, 4).map((event, index) => (
                    <div key={`${event}-${index}`} className="rounded-xl border border-zinc-100 bg-zinc-50 p-3 text-sm text-zinc-700">
                      {event}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-zinc-300 bg-zinc-50 p-5 text-center">
                  <Receipt className="mx-auto text-zinc-400" size={32} />
                  <h3 className="mt-3 font-semibold text-zinc-950">{t('subscription.noBillingRows')}</h3>
                  <p className="mt-2 text-sm leading-6 text-zinc-500">
                    {t('subscription.invoicesUnavailableBody')}
                  </p>
                </div>
              )}
            </div>

            <div className="rounded-2xl border border-zinc-200 bg-white p-5 shadow-sm" data-testid="subscription-faq">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold tracking-tight text-zinc-950">{t('subscription.faq.title')}</h2>
                  <p className="text-sm text-zinc-500">{t('subscription.faqIntro')}</p>
                </div>
                <HelpCircle className="text-lime-700" size={22} />
              </div>
              <div className="space-y-4">
                {FAQ_IDS.map((id) => (
                  <div key={`faq-${id}`} className="rounded-xl border border-zinc-100 bg-zinc-50 p-4">
                    <h4 className="font-semibold text-zinc-950">{t(`subscription.faq.q${id}`)}</h4>
                    <p className="mt-2 text-sm leading-6 text-zinc-500">{t(`subscription.faq.a${id}`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </aside>
        </section>
      </div>
    </div>
  );
};

export default SubscriptionPage;
